import React, { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { resolveMediaUrl } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, Plus, Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import {
  ChefsAdmin,
  TestimonialsAdmin,
  CertificatesAdmin,
  FaqAdmin,
  BlogAdmin,
  InstagramPostsAdmin,
  SettingsAdmin,
} from "@/pages/AdminExtras";

const DAYS = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"];

/* ==========================================================================
 * Reusable: single-file uploader that returns a URL via onUploaded(url)
 * ========================================================================== */
function FileUploader({ kind = "photo", accept, onUploaded, label, testId, small = false }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(data.url, data);
      toast.success(`Uploaded (${data.size_mb} MB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <label
      className={`cursor-pointer inline-flex items-center gap-2 rounded-full border-2 border-dashed border-brand-navy/25 hover:border-brand-orange transition ${small ? "px-3 py-1.5 text-xs" : "px-4 py-2.5 text-sm"} ${busy ? "opacity-70 pointer-events-none" : ""}`}
      data-testid={testId}
    >
      {busy ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
      <span className="text-brand-navy font-medium">{busy ? "Uploading…" : (label || (kind === "video" ? "Upload video" : "Upload photo"))}</span>
      <input
        ref={inputRef}
        type="file"
        accept={accept || (kind === "video" ? "video/*" : "image/*")}
        onChange={handleChange}
        disabled={busy}
        className="hidden"
      />
    </label>
  );
}

/* ==========================================================================
 * Root
 * ========================================================================== */
export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="p-16">…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex items-baseline justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.35em] text-brand-orange font-semibold">Ariaf Admin</div>
          <h1 className="font-serif text-4xl text-brand-navy">Dashboard</h1>
          <div className="mt-1 text-sm text-slate-600">Logged in as {user.email}</div>
        </div>
        <Button variant="outline" onClick={logout} data-testid="admin-logout-btn">Logout</Button>
      </div>

      <Tabs defaultValue="media">
        <TabsList className="bg-canvas-stone flex-wrap h-auto">
          <TabsTrigger value="media" data-testid="admin-tab-media">Media</TabsTrigger>
          <TabsTrigger value="hotels" data-testid="admin-tab-hotels">Hotels</TabsTrigger>
          <TabsTrigger value="menus" data-testid="admin-tab-menus">Menus</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Bookings</TabsTrigger>
          <TabsTrigger value="chefs" data-testid="admin-tab-chefs">Chefs</TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Reviews</TabsTrigger>
          <TabsTrigger value="certs" data-testid="admin-tab-certs">Certificates</TabsTrigger>
          <TabsTrigger value="faqs" data-testid="admin-tab-faqs">FAQ</TabsTrigger>
          <TabsTrigger value="posts" data-testid="admin-tab-posts">Blog</TabsTrigger>
          <TabsTrigger value="instagram" data-testid="admin-tab-instagram">Instagram</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="admins" data-testid="admin-tab-admins">Admins</TabsTrigger>
        </TabsList>

        <TabsContent value="media"><MediaAdmin /></TabsContent>
        <TabsContent value="hotels"><HotelsAdmin /></TabsContent>
        <TabsContent value="menus"><MenusAdmin /></TabsContent>
        <TabsContent value="bookings"><BookingsAdmin /></TabsContent>
        <TabsContent value="chefs"><ChefsAdmin /></TabsContent>
        <TabsContent value="testimonials"><TestimonialsAdmin /></TabsContent>
        <TabsContent value="certs"><CertificatesAdmin /></TabsContent>
        <TabsContent value="faqs"><FaqAdmin /></TabsContent>
        <TabsContent value="posts"><BlogAdmin /></TabsContent>
        <TabsContent value="instagram"><InstagramPostsAdmin /></TabsContent>
        <TabsContent value="settings"><SettingsAdmin /></TabsContent>
        <TabsContent value="admins"><AdminsAdmin currentUser={user} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ==========================================================================
 * MEDIA
 * ========================================================================== */
function MediaAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "photo", url: "", category: "general", caption_tr: "", caption_ar: "", caption_en: "", caption_ru: "", featured: false });
  const [backend, setBackend] = useState("local");

  const load = () => api.get("/media").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/uploads/status").then((r) => setBackend(r.data.backend)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.url) { toast.error("URL required — upload a file or paste a URL"); return; }
    try {
      await api.post("/media", {
        type: form.type,
        url: form.url,
        category: form.category,
        featured: form.featured,
        caption: { tr: form.caption_tr, ar: form.caption_ar, en: form.caption_en, ru: form.caption_ru },
      });
      toast.success("Added");
      setForm({ ...form, url: "", caption_tr: "", caption_ar: "", caption_en: "", caption_ru: "" });
      load();
    } catch (e) { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/media/${id}`);
    load();
  };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="media-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Media</h3>
        <p className="text-xs text-slate-500">Storage: <span className="font-medium">{backend}</span></p>

        <div className="flex flex-wrap gap-2">
          <FileUploader
            kind="photo"
            label="Upload photo"
            testId="media-upload-photo"
            onUploaded={(url) => setForm((f) => ({ ...f, type: "photo", url }))}
          />
          <FileUploader
            kind="video"
            label="Upload video"
            testId="media-upload-video"
            onUploaded={(url) => setForm((f) => ({ ...f, type: "video", url }))}
          />
        </div>

        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="photo">Photo</SelectItem>
            <SelectItem value="video">Video</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Or paste a URL" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="media-url-input" />
        {form.url && (
          <div className="rounded-lg overflow-hidden bg-canvas-stone aspect-video">
            {form.type === "video"
              ? <video src={resolveMediaUrl(form.url)} controls className="w-full h-full object-cover" />
              : <img src={resolveMediaUrl(form.url)} alt="preview" className="w-full h-full object-cover" />}
          </div>
        )}
        <Input placeholder="Category (mezze, kebab, sweets, iftar, hotel, general)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
        <Input placeholder="Caption TR" value={form.caption_tr} onChange={(e) => setForm({ ...form, caption_tr: e.target.value })} />
        <Input placeholder="Caption AR" value={form.caption_ar} onChange={(e) => setForm({ ...form, caption_ar: e.target.value })} />
        <Input placeholder="Caption EN" value={form.caption_en} onChange={(e) => setForm({ ...form, caption_en: e.target.value })} />
        <Input placeholder="Caption RU" value={form.caption_ru} onChange={(e) => setForm({ ...form, caption_ru: e.target.value })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
        <Button type="submit" className="bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="media-submit"><Plus size={14}/> Add</Button>
      </form>

      <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
        {items.map((m) => (
          <div key={m.id} className="relative rounded-xl overflow-hidden group bg-white border border-brand-gold/20">
            {m.type === "video"
              ? <video src={resolveMediaUrl(m.url)} className="w-full aspect-square object-cover" muted />
              : <img src={resolveMediaUrl(m.url)} alt="" className="w-full aspect-square object-cover" />}
            <div className="absolute top-2 left-2 flex gap-1">
              <Badge className="bg-brand-navy text-white text-[10px]">{m.type}</Badge>
              <Badge variant="outline" className="text-[10px] bg-white">{m.category}</Badge>
            </div>
            <button onClick={() => del(m.id)} className="absolute top-2 right-2 bg-white/90 hover:bg-red-50 text-red-600 p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition" data-testid={`media-delete-${m.id}`}>
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
 * HOTELS
 * ========================================================================== */
function HotelsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyHotel());
  const [editingId, setEditingId] = useState(null);

  function emptyHotel() {
    return {
      name: "",
      city: "Makkah",
      distance_to_haram: "",
      rating: 5,
      price_from: 0,
      currency: "USD",
      images: [],
      videos: [],
      amenities: "",
      map_query: "",
      d_tr: "", d_ar: "", d_en: "", d_ru: "",
      h_tr: "", h_ar: "", h_en: "", h_ru: "",
    };
  }

  const load = () => api.get("/hotels").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(emptyHotel()); setEditingId(null); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      city: form.city,
      distance_to_haram: form.distance_to_haram,
      rating: Number(form.rating) || 5,
      price_from: Number(form.price_from) || 0,
      currency: form.currency,
      images: form.images,
      videos: form.videos,
      amenities: form.amenities.split(",").map((s) => s.trim()).filter(Boolean),
      map_query: form.map_query,
      description: { tr: form.d_tr, ar: form.d_ar, en: form.d_en, ru: form.d_ru },
      highlights: { tr: form.h_tr, ar: form.h_ar, en: form.h_en, ru: form.h_ru },
    };
    try {
      if (editingId) {
        await api.put(`/hotels/${editingId}`, payload);
        toast.success("Updated");
      } else {
        await api.post("/hotels", payload);
        toast.success("Saved");
      }
      reset();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const edit = (h) => {
    setForm({
      name: h.name || "",
      city: h.city || "Makkah",
      distance_to_haram: h.distance_to_haram || "",
      rating: h.rating || 5,
      price_from: h.price_from || 0,
      currency: h.currency || "USD",
      images: h.images || [],
      videos: h.videos || [],
      amenities: (h.amenities || []).join(", "),
      map_query: h.map_query || "",
      d_tr: h.description?.tr || "",
      d_ar: h.description?.ar || "",
      d_en: h.description?.en || "",
      d_ru: h.description?.ru || "",
      h_tr: h.highlights?.tr || "",
      h_ar: h.highlights?.ar || "",
      h_en: h.highlights?.en || "",
      h_ru: h.highlights?.ru || "",
    });
    setEditingId(h.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/hotels/${id}`);
    if (editingId === id) reset();
    load();
  };

  const addImage = (url) => setForm((f) => ({ ...f, images: [...f.images, url] }));
  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const addVideo = (url) => setForm((f) => ({ ...f, videos: [...f.videos, url] }));
  const removeVideo = (i) => setForm((f) => ({ ...f, videos: f.videos.filter((_, idx) => idx !== i) }));

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-5">
      <form onSubmit={submit} className="lg:col-span-2 bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="hotel-form">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl text-brand-navy">{editingId ? "Edit Hotel" : "Add Hotel"}</h3>
          {editingId && <Button type="button" variant="outline" size="sm" onClick={reset}>Cancel</Button>}
        </div>

        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        <div className="grid grid-cols-2 gap-2">
          <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="Makkah">Makkah</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Distance to Haram (e.g. 150 m)" value={form.distance_to_haram} onChange={(e) => setForm({ ...form, distance_to_haram: e.target.value })} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input placeholder="Rating" type="number" step="0.1" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
          <Input placeholder="Price from" type="number" step="1" value={form.price_from} onChange={(e) => setForm({ ...form, price_from: e.target.value })} />
          <Input placeholder="Currency" value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} />
        </div>

        {/* Photos */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-brand-navy/70">Photos</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {form.images.map((u, i) => (
              <div key={i} className="relative w-20 h-20 rounded-md overflow-hidden group">
                <img src={resolveMediaUrl(u)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 bg-white/95 rounded-full p-0.5 text-red-600 opacity-0 group-hover:opacity-100" data-testid={`hotel-photo-remove-${i}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
            <FileUploader kind="photo" small label="+ Photo" testId="hotel-photo-upload" onUploaded={addImage} />
          </div>
        </div>

        {/* Videos */}
        <div>
          <Label className="text-xs uppercase tracking-widest text-brand-navy/70">Videos</Label>
          <div className="mt-2 flex flex-wrap gap-2">
            {form.videos.map((u, i) => (
              <div key={i} className="relative w-28 h-20 rounded-md overflow-hidden group bg-black">
                <video src={resolveMediaUrl(u)} muted className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeVideo(i)} className="absolute top-0.5 right-0.5 bg-white/95 rounded-full p-0.5 text-red-600 opacity-0 group-hover:opacity-100" data-testid={`hotel-video-remove-${i}`}>
                  <Trash2 size={12}/>
                </button>
              </div>
            ))}
            <FileUploader kind="video" small label="+ Video" testId="hotel-video-upload" onUploaded={addVideo} />
          </div>
        </div>

        <Input placeholder="Amenities (comma separated)" value={form.amenities} onChange={(e) => setForm({ ...form, amenities: e.target.value })} />

        <Input placeholder="Google Maps place query (e.g. 'Diyar Ajyad Hotel Makkah')" value={form.map_query} onChange={(e) => setForm({ ...form, map_query: e.target.value })} data-testid="hotel-map-query" />

        <div className="pt-2 border-t border-brand-navy/10">
          <Label className="text-xs uppercase tracking-widest text-brand-navy/70">Description</Label>
          <Textarea placeholder="TR" value={form.d_tr} onChange={(e) => setForm({ ...form, d_tr: e.target.value })} className="mt-1" />
          <Textarea placeholder="AR" value={form.d_ar} onChange={(e) => setForm({ ...form, d_ar: e.target.value })} className="mt-1" dir="rtl" />
          <Textarea placeholder="EN" value={form.d_en} onChange={(e) => setForm({ ...form, d_en: e.target.value })} className="mt-1" />
          <Textarea placeholder="RU" value={form.d_ru} onChange={(e) => setForm({ ...form, d_ru: e.target.value })} className="mt-1" />
        </div>

        <div>
          <Label className="text-xs uppercase tracking-widest text-brand-navy/70">Highlights</Label>
          <Textarea placeholder="TR (öne çıkanlar)" value={form.h_tr} onChange={(e) => setForm({ ...form, h_tr: e.target.value })} rows={2} className="mt-1" />
          <Textarea placeholder="AR" value={form.h_ar} onChange={(e) => setForm({ ...form, h_ar: e.target.value })} rows={2} className="mt-1" dir="rtl" />
          <Textarea placeholder="EN" value={form.h_en} onChange={(e) => setForm({ ...form, h_en: e.target.value })} rows={2} className="mt-1" />
          <Textarea placeholder="RU" value={form.h_ru} onChange={(e) => setForm({ ...form, h_ru: e.target.value })} rows={2} className="mt-1" />
        </div>

        <Button type="submit" className="bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="hotel-submit">
          <Plus size={14}/> {editingId ? "Save Changes" : "Add Hotel"}
        </Button>
      </form>

      <div className="lg:col-span-3 space-y-4">
        {items.map((h) => (
          <div key={h.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex gap-4" data-testid={`hotel-item-${h.id}`}>
            <img src={resolveMediaUrl(h.images?.[0])} className="w-28 h-28 object-cover rounded-lg" alt="" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-widest text-brand-orange">{h.city} · {h.distance_to_haram}</div>
              <div className="font-serif text-xl text-brand-navy">{h.name}</div>
              <div className="text-sm text-slate-600 line-clamp-2">{h.description?.en}</div>
              <div className="mt-1 text-xs text-slate-500">
                {h.images?.length || 0} photo(s) · {h.videos?.length || 0} video(s) · ${h.price_from} · ★ {h.rating}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button size="sm" variant="outline" onClick={() => edit(h)} data-testid={`hotel-edit-${h.id}`}>Edit</Button>
              <button onClick={() => del(h.id)} className="text-red-600 self-end p-2" data-testid={`hotel-delete-${h.id}`}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
 * MENUS
 * ========================================================================== */
function MenusAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(makeEmpty());

  function makeEmpty() {
    return {
      l_tr: "", l_ar: "", l_en: "", l_ru: "",
      start_date: "", end_date: "",
      published: true,
      meals: DAYS.map((d) => ({
        day: d,
        breakfast: { tr: "", ar: "", en: "", ru: "" },
        lunch: { tr: "", ar: "", en: "", ru: "" },
        dinner: { tr: "", ar: "", en: "", ru: "" },
        video_url: "",
        image_url: "",
      })),
    };
  }

  const load = () => api.get("/menus").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const updateMeal = (di, meal, lang, value) => {
    const meals = [...form.meals];
    meals[di] = { ...meals[di], [meal]: { ...meals[di][meal], [lang]: value } };
    setForm({ ...form, meals });
  };

  const updateDayMedia = (di, key, value) => {
    const meals = [...form.meals];
    meals[di] = { ...meals[di], [key]: value };
    setForm({ ...form, meals });
  };

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/menus", {
      label: { tr: form.l_tr, ar: form.l_ar, en: form.l_en, ru: form.l_ru },
      start_date: form.start_date,
      end_date: form.end_date,
      published: form.published,
      meals: form.meals,
    });
    setForm(makeEmpty());
    toast.success("Menu created");
    load();
  };

  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/menus/${id}`); load(); };

  return (
    <div className="mt-6 space-y-8">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="menu-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Weekly Menu</h3>
        <div className="grid md:grid-cols-4 gap-3">
          <Input placeholder="Label TR (e.g. 1. Hafta - Ocak)" value={form.l_tr} onChange={(e) => setForm({ ...form, l_tr: e.target.value })} />
          <Input placeholder="Label AR" value={form.l_ar} onChange={(e) => setForm({ ...form, l_ar: e.target.value })} dir="rtl" />
          <Input placeholder="Label EN" value={form.l_en} onChange={(e) => setForm({ ...form, l_en: e.target.value })} />
          <Input placeholder="Label RU" value={form.l_ru} onChange={(e) => setForm({ ...form, l_ru: e.target.value })} />
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required /></div>
          <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required /></div>
        </div>
        <div className="mt-4 space-y-3">
          {form.meals.map((m, di) => (
            <div key={m.day} className="border rounded-xl p-4 bg-canvas-cream/40">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="font-medium text-brand-navy">{m.day}</div>
                <div className="flex flex-wrap items-center gap-2">
                  <FileUploader kind="photo" small label={m.image_url ? "Replace photo" : "+ Photo"} testId={`menu-${m.day}-photo`} onUploaded={(url) => updateDayMedia(di, "image_url", url)} />
                  <FileUploader kind="video" small label={m.video_url ? "Replace video" : "+ Video"} testId={`menu-${m.day}-video`} onUploaded={(url) => updateDayMedia(di, "video_url", url)} />
                  {(m.image_url || m.video_url) && (
                    <div className="flex items-center gap-1 text-xs text-slate-600">
                      {m.image_url && <img src={resolveMediaUrl(m.image_url)} alt="" className="w-8 h-8 object-cover rounded"/>}
                      {m.video_url && <video src={resolveMediaUrl(m.video_url)} muted className="w-10 h-8 object-cover rounded bg-black"/>}
                    </div>
                  )}
                </div>
              </div>
              {["breakfast", "lunch", "dinner"].map((meal) => (
                <div key={meal} className="grid md:grid-cols-4 gap-2 mb-2">
                  <Input placeholder={`${meal} TR`} value={m[meal].tr} onChange={(e) => updateMeal(di, meal, "tr", e.target.value)} />
                  <Input placeholder={`${meal} AR`} value={m[meal].ar} onChange={(e) => updateMeal(di, meal, "ar", e.target.value)} dir="rtl" />
                  <Input placeholder={`${meal} EN`} value={m[meal].en} onChange={(e) => updateMeal(di, meal, "en", e.target.value)} />
                  <Input placeholder={`${meal} RU`} value={m[meal].ru} onChange={(e) => updateMeal(di, meal, "ru", e.target.value)} />
                </div>
              ))}
            </div>
          ))}
        </div>
        <Button type="submit" className="bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="menu-submit"><Plus size={14}/> Save Menu</Button>
      </form>

      <div className="grid gap-4">
        {items.map((m) => (
          <div key={m.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 flex justify-between items-center">
            <div>
              <div className="font-serif text-lg text-brand-navy">{m.label?.en || m.label?.tr}</div>
              <div className="text-sm text-slate-600">{m.start_date} → {m.end_date}</div>
            </div>
            <button onClick={() => del(m.id)} className="text-red-600" data-testid={`menu-delete-${m.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==========================================================================
 * BOOKINGS
 * ========================================================================== */
function BookingsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/bookings").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const changeStatus = async (id, status) => { await api.patch(`/bookings/${id}`, { status }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/bookings/${id}`); load(); };

  return (
    <div className="mt-6 bg-white rounded-2xl border border-brand-gold/20 overflow-hidden" data-testid="bookings-table">
      <table className="w-full text-sm">
        <thead className="bg-canvas-stone text-brand-navy">
          <tr>
            <th className="px-4 py-3 text-start">Name</th>
            <th className="px-4 py-3 text-start">Phone</th>
            <th className="px-4 py-3 text-start">Dates</th>
            <th className="px-4 py-3 text-start">Guests</th>
            <th className="px-4 py-3 text-start">Status</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((b) => (
            <tr key={b.id} className="border-t" data-testid={`booking-row-${b.id}`}>
              <td className="px-4 py-3">
                <div className="font-medium text-brand-navy">{b.name}</div>
                <div className="text-xs text-slate-500">{b.notes}</div>
              </td>
              <td className="px-4 py-3">{b.phone}<div className="text-xs text-slate-500">{b.whatsapp}</div></td>
              <td className="px-4 py-3">{b.arrival_date} → {b.departure_date}</td>
              <td className="px-4 py-3">{b.guests}</td>
              <td className="px-4 py-3">
                <Select value={b.status} onValueChange={(v) => changeStatus(b.id, v)}>
                  <SelectTrigger className="w-36" data-testid={`booking-status-${b.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </td>
              <td className="px-4 py-3 text-end">
                <button onClick={() => del(b.id)} className="text-red-600" data-testid={`booking-delete-${b.id}`}><Trash2 size={16}/></button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-500">No bookings yet</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ==========================================================================
 * ADMINS
 * ========================================================================== */
function AdminsAdmin({ currentUser }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [pwd, setPwd] = useState({});

  const load = () => api.get("/admins").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admins", form);
      toast.success("Admin added");
      setForm({ email: "", name: "", password: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const changePwd = async (id) => {
    const p = pwd[id];
    if (!p || p.length < 8) { toast.error("Min 8 characters"); return; }
    try {
      await api.patch(`/admins/${id}`, { password: p });
      toast.success("Password updated");
      setPwd((s) => ({ ...s, [id]: "" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete admin?")) return;
    try {
      await api.delete(`/admins/${id}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="mt-6 grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="bg-white p-6 rounded-2xl border border-brand-gold/20 grid gap-3" data-testid="admin-user-form">
        <h3 className="font-serif text-xl text-brand-navy">Add Admin</h3>
        <p className="text-xs text-slate-600">Yeni yönetici aynı yetkilere sahip olur. Şifreyi buradan siz belirlersiniz.</p>
        <Input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="admin-user-email" />
        <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-user-name" />
        <PasswordInput placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="admin-user-password" />
        <Button type="submit" className="bg-brand-orange hover:bg-brand-orange-dark text-white" data-testid="admin-user-submit"><Plus size={14}/> Add Admin</Button>
      </form>

      <div className="lg:col-span-2 space-y-3" data-testid="admin-user-list">
        {items.map((a) => {
          const isSelf = a.id === currentUser?.id;
          return (
            <div key={a.id} className="bg-white p-4 rounded-2xl border border-brand-gold/20 grid gap-3 md:grid-cols-[1fr_auto] items-center" data-testid={`admin-user-row-${a.id}`}>
              <div>
                <div className="font-medium text-brand-navy">{a.email} {isSelf && <span className="text-xs text-brand-orange ms-2">(you)</span>}</div>
                <div className="text-sm text-slate-500">{a.name} · {a.role}</div>
              </div>
              <div className="flex items-center gap-2">
                <PasswordInput
                  placeholder="New password"
                  className="w-40"
                  value={pwd[a.id] || ""}
                  onChange={(e) => setPwd((s) => ({ ...s, [a.id]: e.target.value }))}
                  data-testid={`admin-user-pwd-${a.id}`}
                />
                <Button type="button" variant="outline" onClick={() => changePwd(a.id)} data-testid={`admin-user-pwd-save-${a.id}`}>Save</Button>
                {!isSelf && (
                  <button onClick={() => del(a.id)} className="text-red-600 p-2" data-testid={`admin-user-delete-${a.id}`}><Trash2 size={16}/></button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
